import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { startSurvey } from '@intercom/messenger-js-sdk';
import { getEnvVar } from '@studio/environment/getEnvVar';
import {
  startStreams,
  stopStreams,
  startStream
} from '@shoflo/studio-redux/actions';
import { useStreamSLI } from '@studio/metrics/hooks/useStreamSLI';
import { useRouteMatch } from 'react-router-dom';
import { StudioLogger } from '@studio/logger/StudioLogger';
import {
  RESET_BROADCAST_STREAM_START_FAILED_STATUS,
  RESET_BROADCAST_STREAM_STOP_FAILED_STATUS
} from '@shoflo/studio-redux/actions/types';
import {
  endStreamStartMetric,
  endStreamStopMetric
} from 'utils/metric/streamSLIHelper';
import {
  checkStreamStartFailedStatus,
  checkStreamStopFailedStatus
} from './utils';
import {
  isEndpointActive,
  isEndpointStarting,
  isEndpointStopping
} from '../useStreamButtonState/utils';
import { debounce } from 'underscore';

const logger = new StudioLogger('stream-action-utils');
const wait = 3000;

export function useStreamActionUtils() {
  const streamEndpoints = useSelector(
    ({ streamEndpoints }) => streamEndpoints,
    shallowEqual
  );

  const {
    startStreamStartSLI,
    startStreamEndSLI,
    stopStreamStartSLI,
    stopStreamEndSLI,
    startStartAllStreamsMetric,
    endStartAllStreamsMetric
  } = useStreamSLI();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const sliStartedStreamIds = useMemo(() => new Set(), []);
  const sliStoppedStreamIds = useMemo(() => new Set(), []);

  useEffect(() => {
    if (sliStartedStreamIds.size > 0) {
      endStreamStartMetric(
        streamEndpoints,
        startStreamEndSLI,
        sliStartedStreamIds,
        endStartAllStreamsMetric
      );
    }

    if (sliStoppedStreamIds.size > 0) {
      endStreamStopMetric(
        streamEndpoints,
        stopStreamEndSLI,
        sliStoppedStreamIds
      );
    }
  }, [
    startStreamEndSLI,
    stopStreamEndSLI,
    streamEndpoints,
    sliStartedStreamIds,
    sliStoppedStreamIds,
    endStartAllStreamsMetric
  ]);

  const getInactiveEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      endpoint =>
        !isEndpointActive(endpoint) &&
        !isEndpointStopping(endpoint) &&
        !isEndpointStarting(endpoint)
    );
  }, [streamEndpoints]);

  const getActiveEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      endpoint =>
        isEndpointActive(endpoint) ||
        (!isEndpointStopping(endpoint) && !isEndpointStarting(endpoint))
    );
  }, [streamEndpoints]);

  const getTransitioningEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      endpoint => isEndpointStopping(endpoint) || isEndpointStarting(endpoint)
    );
  }, [streamEndpoints]);

  const formattedStreams = useMemo(() => {
    return streamEndpoints?.map(({ _id, title, startedAt, isStreaming }) => {
      return {
        _id,
        title,
        startedAt,
        isStreaming
      };
    });
  }, [streamEndpoints]);

  const onStartStreams = useCallback(() => {
    logger.info('Studio Stream Start - Beginning Start Stream Flow', {});
    const inActiveStreams = getInactiveEndpoints();
    const inActiveStreamsIds = inActiveStreams?.map(stream => {
      return stream._id;
    });

    if (inActiveStreamsIds?.length) {
      logger.info('Studio Stream Start - Inactive Streams found', {});

      if (checkStreamStartFailedStatus(inActiveStreams)) {
        logger.info(
          'Studio Stream Start - Start failed detected in inactive stream list.',
          {}
        );
        dispatch({
          type: RESET_BROADCAST_STREAM_START_FAILED_STATUS,
          payload: { endpoints: inActiveStreams }
        });
      }
      for (const streamId of inActiveStreamsIds) {
        startStreamStartSLI({
          streamId: streamId,
          pathName: match.path,
          url: match.url
        });
        if (!sliStartedStreamIds.has(streamId)) {
          sliStartedStreamIds.add(streamId);
        }
      }
      startStartAllStreamsMetric({ streamCount: inActiveStreamsIds.length });
      return dispatch(startStreams(inActiveStreamsIds));
    }
  }, [
    getInactiveEndpoints,
    dispatch,
    startStreamStartSLI,
    match.path,
    match.url,
    sliStartedStreamIds,
    startStartAllStreamsMetric
  ]);

  const onStopStreams = useCallback(() => {
    logger.info('Studio Stream Stop - Beginning Stop Stream Flow', {});
    const activeStreams = getActiveEndpoints();
    const activeStreamsIds = activeStreams?.map(stream => stream._id);

    if (activeStreamsIds?.length) {
      logger.info('Studio Stream Stop - Active Streams found', {});

      if (checkStreamStopFailedStatus(activeStreams)) {
        logger.info(
          'Studio Stream Stop - Stop failed detected in inactive stream list.',
          {}
        );
        dispatch({
          type: RESET_BROADCAST_STREAM_STOP_FAILED_STATUS,
          payload: { endpoints: activeStreams }
        });
      }

      // render stop stream survey
      startSurvey(getEnvVar('REACT_APP_INTERCOM_STOP_STREAM_SURVEY_ID'));

      for (const streamId of activeStreamsIds) {
        stopStreamStartSLI({
          streamId: streamId,
          pathName: match.path,
          url: match.url
        });
        if (!sliStoppedStreamIds.has(streamId)) {
          sliStoppedStreamIds.add(streamId);
        }
      }
      return dispatch(stopStreams(activeStreamsIds));
    }
  }, [
    dispatch,
    getActiveEndpoints,
    match.path,
    match.url,
    stopStreamStartSLI,
    sliStoppedStreamIds
  ]);

  const onQuitStreams = useCallback(() => {
    logger.info('Studio Stream Quit - Beginning Quit Stream Flow', {});
    const transitioningEndpoints = getTransitioningEndpoints();
    const transitioningStreamIds = transitioningEndpoints?.map(
      stream => stream._id
    );

    for (const streamId of transitioningStreamIds) {
      if (sliStartedStreamIds.has(streamId)) {
        startStreamEndSLI({
          streamId: streamId
        });
        sliStartedStreamIds.delete(streamId);
      }
    }

    for (const streamId of transitioningStreamIds) {
      stopStreamStartSLI({
        streamId: streamId,
        pathName: match.path,
        url: match.url
      });
      if (!sliStoppedStreamIds.has(streamId)) {
        sliStoppedStreamIds.add(streamId);
      }
    }

    if (transitioningStreamIds?.length) {
      return dispatch(stopStreams(transitioningStreamIds));
    }
  }, [
    dispatch,
    getTransitioningEndpoints,
    stopStreamStartSLI,
    match.path,
    match.url,
    sliStartedStreamIds,
    sliStoppedStreamIds,
    startStreamEndSLI
  ]);

  // a function start stream by stream id, used in ManageStreamButton component
  const retryStreamStart = useCallback(
    streamId => {
      const isStreamInActive = getInactiveEndpoints().some(
        stream => stream._id === streamId
      );
      if (!isStreamInActive) {
        return;
      }

      logger.info(
        'Studio Stream re-start - Begins Start Stream Flow from Manage Stream Button',
        { streamId }
      );

      startStreamStartSLI({
        streamId: streamId,
        pathName: match.path,
        url: match.url,
        isRetry: true
      });
      if (!sliStartedStreamIds.has(streamId)) {
        sliStartedStreamIds.add(streamId);
      }

      return dispatch(startStream(streamId));
    },
    [
      getInactiveEndpoints,
      dispatch,
      sliStartedStreamIds,
      startStreamStartSLI,
      match.path,
      match.url
    ]
  );

  return {
    formattedStreams,
    onStartStreams: debounce(onStartStreams, wait, true),
    onStopStreams: debounce(onStopStreams, wait, true),
    onQuitStreams: debounce(onQuitStreams, wait, true),
    retryStreamStart: debounce(retryStreamStart, wait, true)
  };
}

export default useStreamActionUtils;
